import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import AuthProvider from './context/AuthContext';
import AuthRoute from './components/AuthRoute';
import AppLayout from './components/AppLayout';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import ReportPage from './pages/ReportPage';
import SettingsPage from './pages/SettingsPage';
import KBPage from './pages/KBPage';
import GICPage from './pages/GICPage';

// WRS design tokens
const INK      = '#1a1a2e';
const CREAM    = '#f7f3ee';
const GOLD     = '#c9a84c';
const GOLD_LT  = '#f0e0b0';
const RUST     = '#c0533a';
const SAGE     = '#4a7c6f';
const SURFACE  = '#ffffff';
const BORDER   = '#e2dbd0';
const MUTED    = '#8a8070';

const playfair = '"Playfair Display", Georgia, serif';
const dmSans   = '"DM Sans", "Roboto", "Helvetica", "Arial", sans-serif';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary:    { main: INK,  contrastText: CREAM },
    secondary:  { main: GOLD, contrastText: INK   },
    error:      { main: RUST  },
    warning:    { main: GOLD  },
    success:    { main: SAGE, contrastText: SURFACE },
    background: { default: CREAM, paper: SURFACE },
    text:       { primary: INK, secondary: MUTED },
    divider:    BORDER,
  },
  typography: {
    fontFamily: dmSans,
    h1: { fontFamily: playfair, fontWeight: 700 },
    h2: { fontFamily: playfair, fontWeight: 700 },
    h3: { fontFamily: playfair, fontWeight: 700 },
    h4: { fontFamily: playfair, fontWeight: 700 },
    h5: { fontFamily: playfair, fontWeight: 400 },
    h6: { fontFamily: playfair, fontWeight: 400 },
    button: { textTransform: 'none', fontWeight: 500 },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: CREAM, color: INK },
      },
    },

    // ── Paper / Cards ───────────────────────────────────────────────
    MuiPaper: {
      styleOverrides: {
        root: {
          border: `1px solid ${BORDER}`,
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          borderRadius: 16,
        },
      },
    },

    // ── Buttons ─────────────────────────────────────────────────────
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 24px',
          fontFamily: dmSans,
          fontWeight: 500,
        },
        contained: {
          backgroundColor: INK,
          color: CREAM,
          boxShadow: 'none',
          '&:hover': { backgroundColor: '#2d2d4e', boxShadow: 'none', transform: 'translateY(-1px)' },
        },
        outlined: {
          borderColor: BORDER,
          borderWidth: 1.5,
          color: MUTED,
          '&:hover': { borderColor: INK, color: INK, borderWidth: 1.5, backgroundColor: 'transparent' },
        },
        text: {
          color: MUTED,
          '&:hover': { color: INK, backgroundColor: 'transparent' },
        },
        containedSuccess: {
          backgroundColor: SAGE,
          color: SURFACE,
          '&:hover': { backgroundColor: '#3d6b5e', transform: 'translateY(-1px)' },
        },
        containedError: {
          backgroundColor: RUST,
          '&:hover': { backgroundColor: '#a8432b' },
        },
        containedWarning: {
          backgroundColor: GOLD,
          color: INK,
          '&:hover': { backgroundColor: '#b8953e' },
        },
      },
    },

    // ── Inputs ──────────────────────────────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: CREAM,
          borderRadius: 10,
          fontFamily: dmSans,
          '& fieldset': { borderColor: BORDER, borderWidth: 1.5 },
          '&:hover fieldset': { borderColor: GOLD, borderWidth: 1.5 },
          '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: 2 },
          '&.Mui-focused': { backgroundColor: SURFACE },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: MUTED,
          fontSize: '0.875rem',
          fontWeight: 500,
          '&.Mui-focused': { color: GOLD },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: { backgroundColor: CREAM },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: { backgroundColor: `${CREAM} !important`, borderRadius: '10px 10px 0 0' },
      },
    },

    // ── Table ───────────────────────────────────────────────────────
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: INK,
            color: CREAM,
            fontWeight: 500,
            fontSize: '0.78rem',
            letterSpacing: '0.4px',
            borderBottom: `3px solid ${GOLD}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: `${GOLD_LT}55` },
          '&.Mui-selected':       { backgroundColor: `${GOLD_LT}99` },
          '&.Mui-selected:hover': { backgroundColor: `${GOLD_LT}cc` },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${BORDER}`, fontSize: '0.875rem' },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { color: MUTED, borderTop: `1px solid ${BORDER}` },
      },
    },

    // ── Chip ────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 20, fontWeight: 500, fontSize: '0.75rem' },
      },
    },

    // ── Dialog ──────────────────────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, border: `1px solid ${BORDER}` },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontFamily: playfair, fontSize: '1.25rem', color: INK, paddingBottom: 12 },
      },
    },

    // ── Alert ───────────────────────────────────────────────────────
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
      },
    },

    // ── Tooltip ─────────────────────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: INK,
          color: CREAM,
          fontSize: '0.75rem',
          borderRadius: 6,
        },
      },
    },

    // ── Divider ─────────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: BORDER },
      },
    },
  },
});

const ProtectedLayout = ({ requireAdmin = false }: { requireAdmin?: boolean }) => (
  <AuthRoute requireAdmin={requireAdmin}>
    <AppLayout>
      <Outlet />
    </AppLayout>
  </AuthRoute>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <AuthProvider>
          <Router basename="/vaults">
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route element={<ProtectedLayout />}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/entries/:id" element={<ReportPage />} />
                <Route path="/kb" element={<KBPage />} />
                <Route path="/gics" element={<GICPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>

              <Route element={<ProtectedLayout requireAdmin />}>
                <Route path="/admin" element={<AdminPage />} />
              </Route>
            </Routes>
          </Router>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
